import { IN_ELECTRON } from "@follow/shared/constants"
import { useWhoami, whoamiQueryKey } from "@follow/store/user/hooks"
import { userSyncService } from "@follow/store/user/store"
import { tracker } from "@follow/tracker"
import { clearStorage } from "@follow/utils/ns"
import { FollowAPIError } from "@follow-app/client-sdk"
import type { FetchError } from "ofetch"
import { useEffect } from "react"

import { setLoginModalShow } from "~/atoms/user"
import { QUERY_PERSIST_KEY } from "~/constants"
import { useAuthQuery } from "~/hooks/common"
import {
  deleteUserCustom as deleteUserFn,
  getAccountInfo,
  getSession as refreshBetterAuthSession,
  signOut as signOutFn,
} from "~/lib/auth"
import { ipcServices } from "~/lib/client"
import { clearAuthSessionToken, getAuthSessionToken } from "~/lib/client-session"
import { defineQuery } from "~/lib/defineQuery"
import { reloadAtAppRoot } from "~/lib/reload-at-app-root"
import { clearLocalPersistStoreData } from "~/store/utils/clear"

const sessionCookieRefreshInterval = 1000 * 60 * 60 * 12

let lastSessionCookieRefreshAt = 0
let sessionCookieRefreshPromise: Promise<unknown> | null = null

const hasBetterAuthSessionCookie = () => {
  if (typeof document === "undefined") {
    return false
  }

  return /(?:^|;\s*)(?:better-auth\.session_token|__Secure-better-auth\.session_token)=/.test(
    document.cookie,
  )
}

const isElectronRuntime = () => {
  return IN_ELECTRON || (typeof window !== "undefined" && !!window.electron)
}

const refreshAuthSessionCookie = async () => {
  if (isElectronRuntime() && !getAuthSessionToken()) {
    return
  }

  if (Date.now() - lastSessionCookieRefreshAt < sessionCookieRefreshInterval) {
    return
  }

  sessionCookieRefreshPromise ??= refreshBetterAuthSession()
    .then((result) => {
      if (!result?.error) {
        lastSessionCookieRefreshAt = Date.now()
      }
      return result
    })
    .catch(() => null)
    .finally(() => {
      sessionCookieRefreshPromise = null
    })

  await sessionCookieRefreshPromise
}

export const auth = {
  getSession: () => defineQuery(whoamiQueryKey, () => userSyncService.whoami()),
  getAccounts: () => defineQuery(["auth", "accounts"], () => getAccountInfo()),
}

export const useAccounts = () => {
  return useAuthQuery(auth.getAccounts())
}

export const useSocialAccounts = () => {
  const accounts = useAccounts()
  return {
    ...accounts,
    data: accounts.data?.data?.filter((account) => account.provider !== "credential"),
  }
}

export const useHasPassword = () => {
  const accounts = useAccounts()
  return {
    ...accounts,
    data: !!accounts.data?.data?.find((account) => account.provider === "credential"),
  }
}

export const useSession = (options?: { enabled?: boolean }) => {
  const whoami = useWhoami()
  const { data, isLoading, ...rest } = useAuthQuery(auth.getSession(), {
    retry(failureCount, error) {
      const fetchError = error as FetchError

      if (fetchError.statusCode === undefined) {
        return false
      }

      return !!(3 - failureCount)
    },
    enabled: options?.enabled ?? true,
    refetchOnMount: true,
    // Start-up fetches the session through the same query, and several components mount this
    // hook right after. A short window lets them share that result; a login reloads the page.
    staleTime: 60_000,
    meta: {
      persist: true,
    },
  })
  const { error } = rest
  const fetchError = error as FetchError
  const hasLocalAuthCredential = isElectronRuntime()
    ? !!getAuthSessionToken()
    : hasBetterAuthSessionCookie()
  const session =
    (data === undefined || (data === null && hasLocalAuthCredential)) && whoami
      ? ({ user: whoami } as NonNullable<typeof data>)
      : data

  const getAuthStatus = ():
    "loading" | "authenticated" | "error" | "unauthenticated" | "unknown" => {
    if (session) {
      return "authenticated"
    }

    if (fetchError) {
      return "error"
    }

    if (isLoading) {
      return "loading"
    }

    if (data === null) {
      return "unauthenticated"
    }

    return "unknown"
  }

  return {
    session,
    ...rest,
    status: getAuthStatus(),
  } as const
}

export const useAuthSessionCookieRefresh = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) {
      return
    }

    const refresh = () => {
      if (document.visibilityState !== "hidden") {
        void refreshAuthSessionCookie()
      }
    }

    refresh()

    const interval = window.setInterval(refresh, sessionCookieRefreshInterval)
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)

    return () => {
      window.clearInterval(interval)
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", refresh)
    }
  }, [enabled])
}

export const handleSessionChanges = () => {
  setLoginModalShow(false)
  localStorage.removeItem(QUERY_PERSIST_KEY)
  const authSessionToken = getAuthSessionToken()
  ipcServices?.auth.sessionChanged(authSessionToken ?? undefined)
  window.location.reload()
}

export const signOut = async () => {
  const authSessionToken = getAuthSessionToken()

  try {
    if (IN_ELECTRON) {
      const authService = ipcServices?.auth as
        | ({ signOutRemote?: (token?: string) => Promise<void> } & NonNullable<
            typeof ipcServices
          >["auth"])
        | undefined
      await Promise.allSettled([
        ipcServices?.auth.signOut(),
        authService?.signOutRemote?.(authSessionToken ?? undefined),
      ])
    } else {
      await ipcServices?.auth.signOut()
      await signOutFn()
    }
  } finally {
    clearAuthSessionToken()
    // Clear query cache
    localStorage.removeItem(QUERY_PERSIST_KEY)

    // clear local store data
    await clearLocalPersistStoreData()

    // Clear local storage
    clearStorage()
    // Sign out
    await tracker.manager.clear()
  }

  reloadAtAppRoot()
}

export const deleteUser = async ({ TOTPCode }: { TOTPCode?: string }) => {
  if (!TOTPCode) {
    return
  }
  const res = await deleteUserFn({
    TOTPCode,
  })
  // The auth client resolves with the error instead of throwing. Keep the session when the
  // account was not deleted, and keep the code so the form can tell a wrong TOTP code apart.
  if (res.error) {
    throw new FollowAPIError(
      res.error.message || res.error.statusText,
      res.error.status,
      res.error.code ? String(res.error.code) : undefined,
    )
  }
  await signOut()
}
